// Single source of truth for the student app's left navigation.
// Used by BOTH the dashboard shell (StudentApp) and the in-lesson
// main-menu rail/drawer (LearnNowPage) so the two sidebars can never
// drift apart.
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutGrid, BookOpen, Library, Wallet, Settings, Trophy,
  ClipboardList, BarChart3, GraduationCap,
} from 'lucide-react';
import { getExamPrepColors } from './examPrepTheme';
import { useStudent } from './StudentApp';

export const NAV_ITEMS = [
  { to: 'desk', label: 'Student Desk', icon: LayoutGrid, testid: 'nav-desk' },
  { to: 'mydesk', label: 'MyDesk · Classes', icon: BookOpen, testid: 'nav-mydesk' },
  { to: 'assignments', label: 'Assignments', icon: ClipboardList, testid: 'nav-assignments' },
  { to: 'hub', label: 'DeskeyHUB', icon: Library, testid: 'nav-hub' },
  // gold: true — this one nav item ignores the active student theme, same
  // as every other exam-prep surface (see examPrepTheme.js).
  { to: 'exam-prep', label: 'Exam Prep', icon: GraduationCap, testid: 'nav-exam-prep', gold: true },
  { to: 'analytics', label: 'Insights & Score', icon: BarChart3, testid: 'nav-analytics' },
  { to: 'badges', label: 'Badges & Trophies', icon: Trophy, testid: 'nav-badges' },
  { to: 'credits', label: 'Credits & Recharge', icon: Wallet, testid: 'nav-credits' },
  { to: 'settings', label: 'Settings', icon: Settings, testid: 'nav-settings' },
];

// `onNavigate` (optional) fires on every link click — drawers pass their
// close handler so picking a destination also dismisses the overlay.
export function StudentSidebar({ onNavigate }) {
  const student = useStudent();
  const themeId = student?.theme?.id;
  // Exam Prep keeps its own gold on every theme: the icon when idle, a solid fill when open.
  const { gold, goldDeep } = getExamPrepColors(themeId);
  return (
    <nav className="space-y-1" data-testid="student-sidebar">
      {NAV_ITEMS.map(it => (
        <NavLink key={it.to} to={`/student/${it.to}`}
          data-testid={it.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 h-9 px-3 rounded-lg text-sm font-medium transition-[background-color,box-shadow] ${isActive ? 'shadow-[0_4px_12px_-6px_color-mix(in_srgb,var(--st-primary)_70%,transparent)]' : 'hover:bg-[color-mix(in_srgb,var(--st-primary)_9%,transparent)]'}`
          }
          style={({ isActive }) => {
            if (!isActive) return { color: 'var(--st-ink)' };
            return it.gold
              ? { background: goldDeep, color: '#FFFFFF' }
              : { background: 'var(--st-primary)', color: 'var(--st-on-primary)' };
          }}>
          {({ isActive }) => (
            <>
              <it.icon className="w-4 h-4 flex-shrink-0" style={isActive ? undefined : { color: it.gold ? gold : 'var(--st-mute)' }} />
              <span className="truncate">{it.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
